import { type JSX, useCallback, useEffect, useRef, useState } from 'react';
import { type ChainflipChain, type ChainflipAsset } from '@chainflip/utils/chainflip';
import { isNotNullish } from '@chainflip/utils/guard';
import { useWallet as useSolanaWallet } from '@solana/wallet-adapter-react';
import { AnimatePresence, motion } from 'motion/react';
import { useDisconnect as useEvmDisconnect } from 'wagmi';
import { create } from 'zustand';
import { type ChainflipToken } from '@/shared/assets/tokens';
import { DashboardModal } from '@/shared/components/dashboards/Modal';
import { useAllBoostPools } from '@/shared/hooks/useAllBoostPools';
import { useGqlQuery } from '@/shared/hooks/useGqlQuery';
import { usePolkadot } from '@/shared/hooks/usePolkadot';
import useToast from '@/shared/hooks/useToast';
import useTracking from '@/shared/hooks/useTracking';
import { defaultAnimationProps } from '@/shared/utils/consts';
import { chainflipAssetMap } from '@/shared/utils/env';
import TokenAmount from '@/shared/utils/TokenAmount';
import { BoostOptInModalContent } from './BoostOptInModalContent';
import { DepositChannelInfo } from './DepositChannelInfo';
import { DepositChannelLoading } from './DepositChannelLoading';
import { DepositSuccess } from './DepositSuccess';
import { getDepositsForSpecificLiquidityDepositChannelQuery } from '../../../graphql/explorer/deposits';
import useDepositChannel from '../../../hooks/useDepositChannel';
import useRefundAddresses from '../../../hooks/useRefundAddresses';
import { LiquidityProvisionEvents, type LiquidityProvisionTrackEvents } from '../../../types/track';
import { SelectAssetModalContent } from '../../SelectAssetModalContent';
import { RefundAddressModalContent } from '../SetRefundAddressModal';

type Deposit = { token: ChainflipToken; amount: TokenAmount };

type DepositAssetModalStore = {
  depositAsset: ChainflipAsset | undefined;
  depositModalOpen: boolean;
  returnToText: string;
  setDepositAssetAndOpen: (asset: ChainflipAsset, returnToText?: string) => void;
  setDepositAsset: (asset: ChainflipAsset | undefined) => void;
  openDepositModal: (returnToText?: string) => void;
  closeAndResetDepositModal: () => void;
};

export const useDepositAssetModalStore = create<DepositAssetModalStore>((set) => ({
  depositAsset: undefined,
  depositModalOpen: false,
  returnToText: 'Return to LP app',
  setDepositAssetAndOpen: (asset, returnToText) =>
    set({ depositAsset: asset, depositModalOpen: true, ...(returnToText && { returnToText }) }),
  setDepositAsset: (asset) => set({ depositAsset: asset }),
  openDepositModal: (returnToText) =>
    set({ depositModalOpen: true, ...(returnToText && { returnToText }) }),
  closeAndResetDepositModal: () =>
    set({ depositAsset: undefined, depositModalOpen: false, returnToText: 'Return to LP app' }),
}));

export const DepositAssetModal = (): JSX.Element => {
  const {
    depositAsset,
    depositModalOpen,
    returnToText,
    closeAndResetDepositModal,
    setDepositAsset,
  } = useDepositAssetModalStore();

  const track = useTracking<LiquidityProvisionTrackEvents>();
  const { selectedAccount: polkadotAccount } = usePolkadot(true);
  const { disconnect: disconnectEvmWallet } = useEvmDisconnect();
  const { disconnect: disconnectSolanaWallet } = useSolanaWallet();
  const focusRef = useRef<HTMLButtonElement>(null);

  const [selectedToken, setSelectedToken] = useState<ChainflipToken>();
  const [finalisedDeposit, setFinalisedDeposit] = useState<Deposit>();
  const [pendingDeposit, setPendingDeposit] = useState<Deposit>();
  const [boostModalOpen, setBoostModalOpen] = useState<boolean | undefined>(false);

  const extrinsic = 'open-channel' as const;
  const channelToast = useToast(extrinsic);
  const depositToast = useToast('channel-deposit');

  const { boostPoolAvailableAmounts, boostPools } = useAllBoostPools();
  const maxBoostFeePips = boostPools && Math.max(...boostPools.map((p) => p.feeTierPips));

  const {
    createDepositChannel,
    resetDepositChannelState,
    depositChannel,
    depositChannelLoading,
    pendingDeposits,
  } = useDepositChannel({
    onError: () => setSelectedToken(undefined),
  });

  const { data: depositData } = useGqlQuery(getDepositsForSpecificLiquidityDepositChannelQuery, {
    variables: {
      chain: depositChannel?.asset.chain.chainflipId,
      channelId: depositChannel?.channelId.toString(),
      issuedBlockId: depositChannel?.blockNumber,
    },
    context: { clientName: 'processor' },
    refetchInterval: 5000,
    enabled: !(!depositModalOpen || depositChannel == null),
  });

  useEffect(() => {
    const deposits = depositData?.channels?.nodes[0]?.deposits?.nodes;
    if (deposits && deposits.length > 0 && selectedToken) {
      const amount = new TokenAmount(deposits[0].depositAmount, depositChannel?.asset.decimals);
      setFinalisedDeposit({
        amount,
        token: selectedToken,
      });
      track(LiquidityProvisionEvents.DepositSuccess, {
        props: {
          path: window.location.pathname,
          scAccount: polkadotAccount?.idSs58 ?? '-',
          position: 'deposit_asset_modal',
          asset: depositChannel?.asset.symbol ?? '-',
          depositAddress: depositChannel?.depositAddress ?? '-',
          depositAmount: amount.toFixed(),
        },
      });
      depositToast.success(
        `Your deposit of ${amount.toFormatted()} ${selectedToken.name} has arrived!`,
      );
    } else if (pendingDeposits.length > 0 && selectedToken) {
      const amount = new TokenAmount(pendingDeposits[0].amount, depositChannel?.asset.decimals);
      setPendingDeposit({ amount, token: selectedToken });
      track(LiquidityProvisionEvents.PendingDeposit, {
        props: {
          path: window.location.pathname,
          scAccount: polkadotAccount?.idSs58 ?? '-',
          position: 'deposit_asset_modal',
          asset: depositChannel?.asset.symbol ?? '-',
          depositAddress: depositChannel?.depositAddress ?? '-',
          depositAmount: amount.toFixed(),
        },
      });
      depositToast.success(
        `Your deposit of ${amount.toFormatted()} ${selectedToken.name} is on its way!`,
      );
    }
  }, [depositChannel, depositData, selectedToken, pendingDeposits.length]);

  const refundAddresses = useRefundAddresses();

  const selectToken = useCallback(
    (token: ChainflipToken) => {
      setSelectedToken(token);

      if (isNotNullish(refundAddresses?.[token.chain.chainflipId as ChainflipChain])) {
        const availableAmount = boostPoolAvailableAmounts?.[token.chainflipId];
        if (availableAmount) {
          setBoostModalOpen(true);
        } else {
          createDepositChannel({ token });
        }
      }
    },
    [boostPoolAvailableAmounts, refundAddresses],
  );

  useEffect(() => {
    if (!depositModalOpen) {
      depositToast.dismiss();
      channelToast.dismiss();
    } else if (depositAsset) {
      selectToken(chainflipAssetMap[depositAsset]);
    }
  }, [depositModalOpen, depositAsset]);

  let content;

  if (depositChannel && finalisedDeposit) {
    content = (
      <motion.div key="deposit-success" {...defaultAnimationProps}>
        <DepositSuccess
          onClose={closeAndResetDepositModal}
          channel={depositChannel}
          amount={finalisedDeposit.amount}
          returnToText={returnToText}
          isWitnessed
        />
      </motion.div>
    );
  } else if (depositChannel && pendingDeposit) {
    content = (
      <motion.div key="pending-deposit" {...defaultAnimationProps}>
        <DepositSuccess
          onClose={closeAndResetDepositModal}
          channel={depositChannel}
          amount={pendingDeposit.amount}
          returnToText={returnToText}
          isWitnessed={false}
        />
      </motion.div>
    );
  } else if (depositChannel) {
    content = (
      <motion.div key="channel-info" {...defaultAnimationProps}>
        <DepositChannelInfo
          onClose={closeAndResetDepositModal}
          onDepositSent={(amount) => setPendingDeposit({ amount, token: depositChannel.asset })}
          onTokenClick={() => {
            setSelectedToken(undefined);
            resetDepositChannelState();
          }}
          channel={depositChannel}
        />
      </motion.div>
    );
  } else if (depositChannelLoading) {
    content = (
      <motion.div key="channel-loading" {...defaultAnimationProps}>
        <DepositChannelLoading onClose={closeAndResetDepositModal} />
      </motion.div>
    );
  } else if (selectedToken) {
    content = boostModalOpen ? (
      <motion.div key="boost" {...defaultAnimationProps}>
        <BoostOptInModalContent
          // TODO: fix token types to avoid these casts
          asset={selectedToken.chainflipId as ChainflipAsset}
          availableAmount={boostPoolAvailableAmounts[selectedToken.chainflipId]}
          maxBoostFeePips={maxBoostFeePips}
          onAccept={() => createDepositChannel({ token: selectedToken, boostFeeBps: 30 })}
          onDecline={() => createDepositChannel({ token: selectedToken })}
        />
      </motion.div>
    ) : (
      <motion.div key="refund-address" {...defaultAnimationProps}>
        <RefundAddressModalContent
          focusRef={focusRef}
          asset={selectedToken.chainflipId}
          onCancel={closeAndResetDepositModal}
          onComplete={() => {
            createDepositChannel({ token: selectedToken });
          }}
          open={depositModalOpen}
        />
      </motion.div>
    );
  } else {
    content = (
      <motion.div key="select-asset" {...defaultAnimationProps}>
        <SelectAssetModalContent onClose={closeAndResetDepositModal} selectToken={selectToken} />
      </motion.div>
    );
  }

  return (
    <DashboardModal
      initialFocus={focusRef}
      beforeEnter={() => {
        // disconnect wallets to always start deposit flow on qr code screen
        // TODO: change this once we have a global wallet connection dropdown
        //  https://linear.app/chainflip/issue/WEB-1889/add-wallet-connection-to-make-deposits
        if (polkadotAccount.type !== 'evm') disconnectEvmWallet();
        disconnectSolanaWallet();
      }}
      afterLeave={() => {
        resetDepositChannelState();
        setSelectedToken(undefined);
        setFinalisedDeposit(undefined);
        setDepositAsset(undefined);
        setPendingDeposit(undefined);
        setBoostModalOpen(false);
      }}
      open={depositModalOpen}
      onClose={closeAndResetDepositModal}
    >
      <AnimatePresence mode="wait">{content}</AnimatePresence>
    </DashboardModal>
  );
};
